import React, { useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import HelpTooltip from "../../constant/HelpToolTip";
import { createReservation } from "../../../services/OtherService";
import { searchMaterial } from "../../../services/ReportService";
import { searchSource } from "../../../services/RackService";

const emptyItem = {
  isValidItem: false,
  product: null,
  quantity: "",
  source: null,
  showRemoveConfirm: false,
};

const CreateReservationModal = ({onAfterSubmit}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [requisition, setRequisition] = useState({
    reservation_type: "SAP",
    reference_no: "",
  });
  const [requisitionItems, setRequisitionItems] = useState([emptyItem]);

  const [productOptions, setProductOptions] = useState([]);
  const [sourceOptions, setSourceOptions] = useState([]);
  const [loadingProduct, setLoadingProduct] = useState(false);
  const [loadingSource, setLoadingSource] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const openModal = async () => {
    setIsModalOpen(true);
    try {
      setLoadingProduct(true);
      const [materialRes, sourceRes] = await Promise.all([
        searchMaterial(),
        searchSource(),
      ]);
      setProductOptions(materialRes.data.data);
      setSourceOptions(sourceRes.data.data);
    } catch (error) {
      console.error("Error loading options", error);
    } finally {
      setLoadingProduct(false);
      setLoadingSource(false);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  const resetForm = () => {
    setRequisition({ reference_no: "", fulfillment_date: "" });
    setRequisitionItems([emptyItem]);
  };

  const updateRequisition = (key, value) => {
    setRequisition({ ...requisition, [key]: value });
    if (!value) {
      document.getElementById(key)?.classList.add("is-invalid");
    } else {
      document.getElementById(key)?.classList.remove("is-invalid");
    }
  };

  const updateItem = (index, key, value) => {
    const newItems = [...requisitionItems];
    const updatedFields = { [key]: value };
    const updatedItem = { ...newItems[index], ...updatedFields };
    updatedItem.isValidItem = validateItem(updatedItem, index, [key]);
    newItems[index] = updatedItem;
    setRequisitionItems(newItems);
  };

  const validateItem = (item, index, validationKeys = []) => {
    if (validationKeys.length > 0) {
      validationKeys.forEach((key) => {
        item[key] && item[key]
          ? document.getElementById(`${key}-${index}`)?.classList.remove("is-invalid")
          : document.getElementById(`${key}-${index}`)?.classList.add("is-invalid");
      });
    }
    return item.quantity > 0 && item.source?.value && item.product?.value;
  };

  const validateAll = () => {
    const isreqValid = Object.keys(requisition)
      .map((key) => {
        const isValid = requisition[key] && requisition[key].trim() !== "";
        if (!isValid) {
          document.getElementById(key)?.classList.add("is-invalid");
        }
        return isValid;
      })
      .every((val) => val === true);

    const isItemsValid = requisitionItems.every((item, index) => {
      return validateItem(item, index, ["product", "quantity", "source"]);
    });

    return isreqValid && isItemsValid;
  };

  const addNewItem = () => {
    if (validateAll()) {
      setRequisitionItems([...requisitionItems, emptyItem]);
    }
  };

  const removeItem = (index) => {
    const newItems = [...requisitionItems];
    newItems.splice(index, 1);
    setRequisitionItems(newItems);
  };

  const toggleRemoveConfirm = (index, value) => {
    const newItems = [...requisitionItems];
    newItems[index].showRemoveConfirm = value;
    setRequisitionItems(newItems);
  };

  const handleSubmit = async () => {
    if (!validateAll()) return;

    const items = requisitionItems.map((item) => ({
      product_id: item.product.value,
      quantity: Number(item.quantity),
      source: item.source.value,
    }));

    const payload = {
      ...requisition,
      items,
    };

    setIsSubmitting(true);
    try {
      const response = await createReservation(payload);
      toast.success(response.data.message);
      closeModal();
      onAfterSubmit(response.data.data);
    } catch (error) {
      toast.error(error.response?.data?.message || "Something went wrong.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getAvailableProducts = (index) => {
    const selectedValues = requisitionItems
      .map((item, i) => (i !== index ? item.product?.value : null))
      .filter(Boolean);
    return productOptions.filter((opt) => !selectedValues.includes(opt.value));
  };

  return (
    <>
      <button className="btn btn-primary" onClick={openModal}>
        <i className="fa-solid fa-plus me-1" /> Create Reservation
      </button>

      <Modal show={isModalOpen} onHide={closeModal} size="lg" backdrop="static">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Create Reservation</h5>
            <button type="button" className="btn-close" onClick={closeModal} />
          </div>

          <div className="modal-body">
            <div className="row mb-3">
              <div className="col-md-6">
                <label>Reservation Type</label>
                <br />
                <div className="form-check custom-checkbox mb-2 form-check-inline">
                  <input
                    type="radio"
                    className="form-check-input"
                    checked={requisition.reservation_type === "SAP"}
                    id="sap"
                    value={"SAP"}
                    onChange={() => updateRequisition("reservation_type", "SAP")}
                  />
                  <label className="form-check-label" htmlFor="sap">
                    SAP
                  </label>
                </div>
                <div className="form-check custom-checkbox mb-2 form-check-inline">
                  <input
                    type="radio"
                    className="form-check-input"
                    checked={requisition.reservation_type === "T72"}
                    id="t72"
                    value={"T72"}
                    onChange={() =>
                      updateRequisition("reservation_type", "T72")
                    }
                  />
                  <label className="form-check-label" htmlFor="t72">
                    T-72
                  </label>
                </div>
              </div>

              <div className="col-md-6">
                {requisition.reservation_type !== "CASH PURCHASE" ?
                <div>
                  <label className="form-label" htmlFor="reference_no">
                    Reference No <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={requisition.reference_no}
                    onChange={(e) =>
                      updateRequisition("reference_no", e.target.value)
                    }
                    placeholder={`Enter ${requisition.reservation_type === "SAP" ? "SAP Reservation ID" : "PO Number"}`}
                    id={"reference_no"}
                  />
                </div>
                : null}
              </div>
            </div>

            <h5>Add Materials :</h5>

            {requisitionItems.map((item, index) => (
              <div className="row mb-2" key={index} id={`row-${index}`}>
                <div className="col-md-4">
                  <div className="d-flex align-items-center gap-2">
                    <div className="w-100">
                      <Select
                        value={item.product}
                        options={getAvailableProducts(index)}
                        isLoading={loadingProduct}
                        placeholder="Select Material"
                        onChange={(val) => updateItem(index, "product", val)}
                        id={`product-${index}`}
                        className={"react-select"}
                        classNamePrefix="select"
                      />
                    </div>
                    {item.product?.label?.length > 25 && (
                      <HelpTooltip text={item.product.label} />
                    )}
                  </div>
                </div>

                <div className="col-md-2">
                  <input
                    type="number"
                    className="form-control"
                    placeholder="Quantity"
                    value={item.quantity}
                    id={`quantity-${index}`}
                    onChange={(e) =>
                      updateItem(index, "quantity", e.target.value)
                    }
                    min="1"
                  />
                </div>

                <div className="col-md-4">
                  <Select
                    value={item.source}
                    options={sourceOptions}
                    isLoading={loadingSource}
                    id={`source-${index}`}
                    placeholder="Select Source"
                    onChange={(val) => updateItem(index, "source", val)}
                    className="react-select"
                    classNamePrefix="select"
                  />
                </div>

                <div className="col-md-2 text-end">
                  {item.isValidItem &&
                    index === requisitionItems.length - 1 && (
                      <button
                        className="btn btn-sm btn-outline-success me-1"
                        onClick={addNewItem}
                      >
                        <i className="fa fa-plus" />
                      </button>
                    )}
                  {requisitionItems.length > 1 &&
                    (item.showRemoveConfirm ? (
                      <>
                        <button
                          className="btn btn-sm btn-outline-success me-1"
                          onClick={() => removeItem(index)}
                        >
                          <i className="fa fa-check" />
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => toggleRemoveConfirm(index, false)}
                        >
                          <i className="fa fa-times" />
                        </button>
                      </>
                    ) : (
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => toggleRemoveConfirm(index, true)}
                      >
                        <i className="fa fa-trash" />
                      </button>
                    ))}
                </div>
              </div>
            ))}
          </div>

          <div className="modal-footer">
            <button
              className="btn btn-primary btn-sm"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Spinner size="sm" /> Creating...
                </>
              ) : (
                "Create Reservation"
              )}
            </button>
            <button
              className="btn btn-secondary btn-sm"
              onClick={closeModal}
              disabled={isSubmitting}
            >
              Close
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default CreateReservationModal;
